'use client'

import { Check, CircleDashed } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchWithAuth } from '@/utils/api-error-handler'
import { setOnboardingCompletedTaskIds } from '@/utils/onboarding'

type OnboardingTask = {
  id: string
  title: string
  description: string
  path: string
  metric_name: string
  order: number
  is_completed: boolean
}

type OnboardingResponse = {
  data: OnboardingTask[]
}

const ONBOARDING_CACHE_TTL_MS = 120_000
let onboardingCache: { data: OnboardingTask[]; at: number } | null = null
let onboardingInFlight: Promise<OnboardingTask[]> | null = null

async function fetchOnboardingShared(url: string, force: boolean): Promise<OnboardingTask[]> {
  const now = Date.now()
  if (!force && onboardingCache && now - onboardingCache.at < ONBOARDING_CACHE_TTL_MS) {
    return onboardingCache.data
  }
  if (onboardingInFlight) {
    return onboardingInFlight
  }

  onboardingInFlight = (async () => {
    const res = await fetchWithAuth(
      url,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      },
      undefined,
      { skipUnauthorizedRedirect: true }
    )
    if (!res.ok) return onboardingCache?.data ?? []
    const json = (await res.json()) as OnboardingResponse
    const list = json.data || []
    onboardingCache = { data: list, at: Date.now() }
    return list
  })().finally(() => {
    onboardingInFlight = null
  })

  return onboardingInFlight
}

export function NavOnboarding() {
  const { state } = useSidebar()
  const router = useRouter()
  const [tasks, setTasks] = useState<OnboardingTask[]>([])
  const [loading, setLoading] = useState(true)
  const isCollapsed = state === 'collapsed'

  const fetchOnboardingStatus = useCallback(async (force: boolean = false) => {
    const API_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, '')
    if (!API_URL) return

    if (tasks.length === 0) setLoading(true)
    try {
      const list = await fetchOnboardingShared(`${API_URL}/v1/user/onboarding-status`, force)
      setTasks(list)
      setOnboardingCompletedTaskIds(list.filter((t) => t.is_completed).map((t) => t.id))
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error)
    } finally {
      setLoading(false)
    }
  }, [tasks.length])

  useEffect(() => {
    void fetchOnboardingStatus(false)

    const handleTaskCompleted = () => {
      void fetchOnboardingStatus(true)
    }

    window.addEventListener('onboarding-task-completed', handleTaskCompleted)

    return () => {
      window.removeEventListener('onboarding-task-completed', handleTaskCompleted)
    }
  }, [fetchOnboardingStatus])

  const completedCount = tasks.filter((t) => t.is_completed).length
  const totalCount = tasks.length
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  // Show skeleton while loading
  if (loading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            className={`${isCollapsed ? 'justify-center' : ''}`}
            disabled
          >
            <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : ''}`}>
              <Skeleton className={`${isCollapsed ? 'h-5 w-5' : 'h-8 w-8'} rounded-full`} />
              {!isCollapsed && <Skeleton className="ml-3 h-4 w-28" />}
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  const allComplete = completedCount === totalCount && totalCount > 0

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={`data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground ${
                isCollapsed ? 'justify-center' : ''
              }`}
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'gap-3'}`}>
                {/* Progress: circle with count, or check when all complete */}
                <div className="relative h-8 w-8 shrink-0 flex items-center justify-center">
                  {allComplete ? (
                    <Check className="h-4 w-4 text-muted-foreground stroke-[3]" />
                  ) : (
                    <>
                      <svg className="h-8 w-8 -rotate-90 absolute inset-0" viewBox="0 0 36 36">
                        <circle
                          cx="18"
                          cy="18"
                          r="16"
                          fill="none"
                          className="stroke-muted"
                          strokeWidth="3"
                        />
                        <circle
                          cx="18"
                          cy="18"
                          r="16"
                          fill="none"
                          className="stroke-primary transition-all duration-300"
                          strokeWidth="3"
                          strokeDasharray={`${completionPercentage} 100`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="text-[9px] font-bold relative z-10">
                        {completedCount}/{totalCount}
                      </span>
                    </>
                  )}
                </div>
                {!isCollapsed && (
                  <div className="flex-1 text-left">
                    <span className="text-xs font-medium">Getting Started</span>
                  </div>
                )}
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[380px] sm:min-w-[380px] sm:max-w-[420px] rounded-xl border bg-background p-0 shadow-xl overflow-hidden"
            side={isCollapsed ? 'right' : 'top'}
            align="start"
            sideOffset={10}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">Getting Started</h3>
                <span className="text-xs text-muted-foreground">
                  {completedCount}/{totalCount}
                </span>
              </div>
              <Progress value={completionPercentage} className="h-1.5" />
              {allComplete ? (
                <p className="text-sm font-medium mt-2 flex items-center gap-2">
                  <span aria-hidden>🎉</span>
                  You&apos;ve completed all tasks!
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-2">
                  {completionPercentage}% complete
                </p>
              )}
            </div>

            {/* Task List with ScrollArea */}
            <ScrollArea className="max-h-[280px]">
              <div className="py-1.5">
                {tasks.map((task, index) => (
                  <div key={task.id}>
                    <DropdownMenuItem
                      className="px-4 py-2.5 cursor-pointer flex items-start gap-3 focus:bg-sidebar-accent"
                      onClick={() => {
                        router.push(task.path)
                      }}
                    >
                      {task.is_completed ? (
                        <Check className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                      ) : (
                        <CircleDashed className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-medium mb-1 ${
                            task.is_completed ? 'text-muted-foreground line-through' : ''
                          }`}
                        >
                        {task.title}
                      </div>
                      {!task.is_completed && (
                        <div className="text-xs text-muted-foreground">
                          {task.description}
                        </div>
                      )}
                    </div>
                    </DropdownMenuItem>
                    {index < tasks.length - 1 && <DropdownMenuSeparator className="my-1 mx-4" />}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
